import { useTranslation } from 'react-i18next';

// Helper function to get card category based on ID
export const getCardCategory = (id: number): string => {
    if (id <= 21) return 'majorArcana';
    if (id <= 35) return 'cups';
    if (id <= 49) return 'pentacles';
    if (id <= 63) return 'swords';
    if (id <= 77) return 'wands';
    return 'majorArcana'; // fallback
};

// Type for translated card data
interface TranslatedCard {
    card_name: string;
    primary_meaning: string;
    interpretation: string;
}

// Dedicated function to get translated card name by ID
export const getCardNameById = (cardId: number, t: any): string => {
    const category = getCardCategory(cardId);
    const cardKey = cardId.toString();

    const translatedCard = t(`cardTranslations.${category}.${cardKey}`, { returnObjects: true }) as TranslatedCard;
    return translatedCard?.card_name || `Card ${cardId}`;
};

// Hook to get translated card information
export const useCardTranslation = (cardId: number) => {
    const { t } = useTranslation();
    const category = getCardCategory(cardId);
    const cardKey = cardId.toString();

    // Get translated card data
    const translatedCard = t(`cardTranslations.${category}.${cardKey}`, { returnObjects: true }) as TranslatedCard;

    return {
        card_name: translatedCard?.card_name || `Card ${cardId}`,
        primary_meaning: translatedCard?.primary_meaning || '',
        interpretation: translatedCard?.interpretation || '',
    };
};

// Function to get translated card name
export const useCardName = (cardId: number): string => {
    const { t } = useTranslation();
    return getCardNameById(cardId, t);
};

// Function to get translated card meaning
export const useCardMeaning = (cardId: number): string => {
    const { t } = useTranslation();
    const category = getCardCategory(cardId);
    const cardKey = cardId.toString();

    const translatedCard = t(`cardTranslations.${category}.${cardKey}`, { returnObjects: true }) as TranslatedCard;
    return translatedCard?.primary_meaning || '';
};

// Function to get translated card interpretation
export const useCardInterpretation = (cardId: number): string => {
    const { t } = useTranslation();
    const category = getCardCategory(cardId);
    const cardKey = cardId.toString();

    const translatedCard = t(`cardTranslations.${category}.${cardKey}`, { returnObjects: true }) as TranslatedCard;
    return translatedCard?.interpretation || '';
};

// Backward compatibility hook that returns getCardName function
export const useCardTranslations = () => {
    const { t } = useTranslation();

    const getCardName = (cardId: number, fallbackName: string): string => {
        return getCardNameById(cardId, t) || fallbackName;
    };

    return { getCardName };
}; 